// Contract of the P1 read of the certificates the Directory publishes for the customer: the
// signing keys of the organisation and the software and the software's mTLS transport key, each
// with the expiry of its x5c leaf. The DCR registration has no expiry of its own; it depends on
// the transport and signing certificates, so this list is also the DCR's.
//
// `@finnest/compliance-watch` owns the keystore files and the severity vocabulary but is
// node-only, so the closed vocabularies are re-declared here and a parity test pins them to it.
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`.
import { Schema } from 'effect';
import { Ecosystem, IsoDateTimeUtc } from '@finnest/core/effect';
import { ComplianceSeverityView } from '../../../compliance-watch/compliance-watch.ts';
import { CustomerId } from '../../vocabulary/ids/branded-ids.ts';

const MAX_KID_CHARS = 200;
const MAX_ISSUER_CHARS = 1024;
// Three keystore files of at most 200 keys each, the bound the keystore reader puts on a file.
export const DIRECTORY_CERTIFICATES_CAP = 600;

const boundedText = (max: number): Schema.String =>
  Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(max));

/** The Directory keystore file a key is published in. */
export const DirectoryKeystoreFileView = Schema.Literals([
  'org-application',
  'software-application',
  'software-transport',
]);
export type DirectoryKeystoreFileView = typeof DirectoryKeystoreFileView.Type;

/** The Directory a read came from: the customer's active one, sandbox until production is chosen. */
export const DirectoryEnvironmentView = Schema.Literals(['sandbox', 'production']);
export type DirectoryEnvironmentView = typeof DirectoryEnvironmentView.Type;

/**
 * A published key with an x5c leaf: `assessed` when the leaf decodes, with its expiry and the
 * severity the regulated window gives it; `unreadable` when it does not, so the key is still shown.
 */
export const DirectoryCertificateView = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal('assessed'),
    file: DirectoryKeystoreFileView,
    kid: boundedText(MAX_KID_CHARS),
    issuer: boundedText(MAX_ISSUER_CHARS),
    notAfter: IsoDateTimeUtc,
    daysRemaining: Schema.Int,
    severity: ComplianceSeverityView,
  }),
  Schema.Struct({
    kind: Schema.Literal('unreadable'),
    file: DirectoryKeystoreFileView,
    kid: boundedText(MAX_KID_CHARS),
  }),
]).annotate({
  title: 'PowerAdminDirectoryCertificate',
  description: 'A key the Directory publishes for the customer and the expiry of its certificate.',
});
export type DirectoryCertificateView = typeof DirectoryCertificateView.Type;

/**
 * `unregistered` while the customer's Directory registration was never captured (P1); otherwise
 * the keys read from the active Directory, unreadable ones first, then by days remaining.
 */
export const DirectoryCertificatesView = Schema.Union([
  Schema.Struct({ kind: Schema.Literal('unregistered') }),
  Schema.Struct({
    kind: Schema.Literal('read'),
    environment: DirectoryEnvironmentView,
    readAt: IsoDateTimeUtc,
    certificates: Schema.Array(DirectoryCertificateView).check(
      Schema.isMaxLength(DIRECTORY_CERTIFICATES_CAP),
    ),
  }),
]).annotate({
  title: 'PowerAdminDirectoryCertificates',
  description:
    'The certificates the Directory publishes for the customer, or that it is not registered.',
});
export type DirectoryCertificatesView = typeof DirectoryCertificatesView.Type;

export const DirectoryCertificatesQuery = Schema.Struct({
  customerId: CustomerId,
  ecosystem: Ecosystem,
});
export type DirectoryCertificatesQuery = typeof DirectoryCertificatesQuery.Type;
